import React from 'react';
import Typography from '@material-ui/core/Typography';
import T from 'i18n-react';
import Input from '../Field/Input';
import {
  validateBoolean,
  validateFirstDayStatus,
  validateHoliday,
  validateString,
  validateTime,
} from './validate';
import Select from '../Field/Select';
import PlanTermContext from './context';
import type { SetPlanTermProp, Validity } from './context';
import { calculateMinHolidays } from '../../../admin/Plans/PlanTerm/functions';

const Meeting = () => {
  const { planTerm, setPlanTerm, isEditing, validity } =
    React.useContext(PlanTermContext);

  const [state, setState] = React.useState(() => {
    const { meetingTime, meetingPlace, isFreeMeeting, firstDayStatus } =
      planTerm;
    return { meetingTime, meetingPlace, isFreeMeeting, firstDayStatus };
  });

  const { startDate, endDate, holidays, lastDayStatus, workingDays } = planTerm;
  const { meetingTime, meetingPlace, isFreeMeeting, firstDayStatus } = state;

  const handleChange = (data: SetPlanTermProp, validity: Validity) => {
    setState(data);
    setPlanTerm(data, validity);
  };

  return (
    <>
      <Typography component="div">
        {T.translate('planPage.planTerms.meetsAt', {
          place: (
            <Input
              textFieldProps={{
                value: meetingPlace,
                onChange: (event) =>
                  handleChange(
                    (prevState) => ({
                      ...prevState,
                      meetingPlace: event.target.value,
                    }),
                    { meetingPlace: validateString(event.target.value, true) },
                  ),
                placeholder: T.translate('planPage.planTerms.meetingPlace'),
                required: true,
                error: validity.meetingPlace === false,
                style: { maxWidth: 150 },
              }}
              readOnly={!isEditing}
            />
          ),
          time: (
            <Input
              textFieldProps={{
                type: 'time',
                value: meetingTime,
                placeholder: T.translate('planPage.planTerms.meetingTime'),
                required: true,
                style: { maxWidth: 150 },
              }}
              timeProps={{
                onChange: (value) =>
                  handleChange(
                    (prevState) => ({
                      ...prevState,
                      meetingTime: value,
                    }),
                    { meetingTime: validateTime(value, true) },
                  ),
                onError: (reason, value) =>
                  handleChange(
                    (prevState) => ({
                      ...prevState,
                      meetingTime: value,
                    }),
                    { meetingTime: !reason },
                  ),
              }}
              readOnly={!isEditing}
            />
          ),
        })}
      </Typography>
      <Select
        selectProps={{
          value: isFreeMeeting == null ? '' : isFreeMeeting ? 1 : 0,
          displayEmpty: true,
          error: validity.isFreeMeeting === false,
          onChange: (event) =>
            handleChange(
              (prevState) => ({
                ...prevState,
                isFreeMeeting: event.target.value,
              }),
              { isFreeMeeting: validateBoolean(event.target.value, true) },
            ),
        }}
        menuItemsProps={[
          {
            value: '',
            disabled: true,
            children: (
              <Typography
                component="span"
                color="textSecondary"
                variant="inherit"
              >
                {T.translate('planPage.planTerms.selectFreeMeetingTime')}
              </Typography>
            ),
          },
          {
            value: 1,
            children: T.translate('planPage.planTerms.isFreeMeetingTime'),
          },
          {
            value: 0,
            children: T.translate('planPage.planTerms.isNotFreeMeetingTime'),
          },
        ]}
        readOnly={!isEditing}
      />
      <Select
        selectProps={{
          value: firstDayStatus == null ? '' : firstDayStatus,
          displayEmpty: true,
          error: validity.firstDayStatus === false,
          onChange: (event) => {
            const firstDayStatus = event.target.value;
            const minHolidays = calculateMinHolidays({
              duration: workingDays + holidays,
              firstDayStatus,
              lastDayStatus,
            });

            handleChange(
              (prevState) => ({
                ...prevState,
                firstDayStatus,
                minHolidays,
              }),
              {
                firstDayStatus: validateFirstDayStatus({
                  firstDayStatus,
                }),
                holiday: validateHoliday({
                  startDate,
                  endDate,
                  holidays,
                  minHolidays,
                }),
              },
            );
          },
        }}
        menuItemsProps={[
          {
            value: '',
            disabled: true,
            children: (
              <Typography
                component="span"
                color="textSecondary"
                variant="inherit"
              >
                {T.translate('planPage.planTerms.selectFirstDayStatus')}
              </Typography>
            ),
          },
          {
            value: 'scheduled',
            children: T.translate('planPage.planTerms.isScheduledFirstDay'),
          },
          {
            value: 'moving',
            children: T.translate('planPage.planTerms.movingDay'),
          },
        ]}
        readOnly={!isEditing}
      />
    </>
  );
};

export default Meeting;
